import type { RequestStatus } from '@features/request/requestSlice';
import { useResetStoreData } from '@features/request/useResetStoreData';
import type { Meta } from '@utils/types';
import { useRequest } from 'features/request/useRequest';
import type {
    BimcCalculationLimitedCostConstructionTypeInput,
    BimcCreateCalculationLimitedCostMutationVariables,
    FileInput,
    ServiceIdResultOfInt64,
} from 'gql/graphql';

import { useCallback } from 'react';
import { useDispatch } from 'react-redux';

import { bimcCreateCalculationLimitedCostGql } from '../bimCalculationLimitedQueries';
import type { ChapterItem } from '../commonData';
import type { IBimLimitedCostFormData } from '../components/BimCalculationLimitedEditModal';

export interface IUseBimCreateCalculationLimitedCost {
    calculationId: number;
    selectedChapter: ChapterItem;
    refreshProject?: () => void;
}

const requestId = 'bimcCreateCalculationLimitedCost';

export const useBimCreateCalculationLimitedCost = (
    props: IUseBimCreateCalculationLimitedCost,
): {
    createCalculationLimitedCostReset: (value: unknown) => { payload: undefined; type: string } & { meta: Meta };
    createCalculationLimitedCostStatus: RequestStatus;
    createCalculationLimitedCostData: ServiceIdResultOfInt64 | undefined;
    createCalculationLimitedCostMakeRequest: (
        data: IBimLimitedCostFormData,
        constructionTypesIds: BimcCalculationLimitedCostConstructionTypeInput[],
        formula: string,
        files?: FileInput[],
    ) => void;
} => {
    const { calculationId, selectedChapter, refreshProject } = props;

    const onSuccessHandler = useCallback(() => {
        refreshProject?.();
    }, [refreshProject]);

    const dispatch = useDispatch();

    const [limitedCostData, status, , bimcCreateCalculationLimitedCostRequest] = useRequest({
        requestId,
        query: bimcCreateCalculationLimitedCostGql,
        withEffect: false,
        dependenciesUpdater: onSuccessHandler,
    });

    const [resetCreate] = useResetStoreData({ requestId });

    const makeRequest = useCallback(
        (
            data: IBimLimitedCostFormData,
            constructionTypesIds: BimcCalculationLimitedCostConstructionTypeInput[],
            formula: string,
            files?: FileInput[],
        ) => {
            const globalLimitedCostId = data?.globalLimitedCost?.id ? Number(data.globalLimitedCost.id) : undefined;

            const variables: BimcCreateCalculationLimitedCostMutationVariables = {
                input: {
                    calculationId,
                    name: (data.name || data?.globalLimitedCost?.name) as string,
                    catalogLimitedCostTypeId: data.limitedCostType?.id,
                    hasCalculatingParticipation: data?.hasCalculatingParticipation?.id || false,
                    isNorthernAllowance: false,
                    formula,
                    key: data.key,
                    constructionTypes: constructionTypesIds,
                    chapter: selectedChapter?.id,
                    globalLimitedCostId,
                    rationale: data?.rationale,
                    file: files?.[0],
                },
            };

            dispatch(bimcCreateCalculationLimitedCostRequest(variables));
        },
        [calculationId, dispatch, selectedChapter?.id, bimcCreateCalculationLimitedCostRequest],
    );

    return {
        createCalculationLimitedCostData: limitedCostData?.bimcCreateCalculationLimitedCost as
            | ServiceIdResultOfInt64
            | undefined,
        createCalculationLimitedCostStatus: status,
        createCalculationLimitedCostReset: resetCreate,
        createCalculationLimitedCostMakeRequest: makeRequest,
    };
};
